import { useApiMutation } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import { ProfileImage, UserProfile } from '@/interface/profile';
import { datadogLogs } from '@datadog/browser-logs';
import { logRocketUtil } from '@/util/logrocket';
import { extractMemberKeys } from '@/util/profileKeys';
import { readStaffProfile } from '@/util/staffUtils';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export interface ProfileContextType {
  profile: UserProfile | null;
  profileStatus: 'IDLE' | 'LOADING' | 'COMPLETED';
  profileImage: string | undefined;
  setProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  setProfileImage: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const initialValue = (userInfo: any): any => ({
  value: [
    {
      MemberFirstName: userInfo?.given_name,
      MemberLastName: userInfo?.family_name,
      MemberEmail: userInfo?.email,
      eXpMember: {
        Uuid: '',
      },
    },
  ],
});

export const ProfileContext = createContext<ProfileContextType | undefined>(
  undefined,
);

const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const { authState, userInfo, isAccessDenied } = useAuth();
  const isStaffEnv = process.env.REACT_APP_ENV === 'staff';

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileImage, setProfileImage] = useState<string | undefined>();
  const [profileStatus, setProfileStatus] = useState<
    'IDLE' | 'LOADING' | 'COMPLETED'
  >('IDLE');

  // Use a ref to track if profile fetch has been initiated
  const profileFetchInitiated = useRef(false);
  // Track whether the staff admin has been identified in LogRocket (prevents re-identification)
  const staffAdminIdentified = useRef(false);

  const profileMutation = useApiMutation<UserProfile, { username: string }>({
    url: '/profile',
    method: 'post',
    options: { retry: 1 },
    endpoint: 'prod',
  });

  const profileImageMutation = useApiMutation<ProfileImage, { userId: string }>(
    {
      url: '/profile-image',
      method: 'post',
      options: { retry: 1 },
      endpoint: 'prod',
    },
  );

  const imagesMutation = useApiMutation<string, { url: string }>({
    url: '/images',
    method: 'post',
    options: { retry: 1 },
    endpoint: 'prod',
  });

  const setLogRocketUserIdentifier = (profile: UserProfile) => {
    const user = profile.value[0];

    datadogLogs.setUser({
      id: user.eXpMember.Uuid,
      ApUserUUID: user.eXpMember.Uuid,
    });
    datadogLogs.setUserProperty('AP-userUUID', user.eXpMember.Uuid);
    // In staff env, LogRocket is always identified with the admin (Okta user),
    // not the impersonated user. The admin identification is handled separately.
    if (!isStaffEnv) {
      logRocketUtil.identifyUser(profile);
    }
  };

  const hydrateFromLocalStorage = useCallback(() => {
    // Returns null for a blob this build cannot trust — including one written
    // before a field was added to the mapped profile, which would otherwise
    // rehydrate silently and send an incomplete /chat_stream payload.
    const storedProfile = readStaffProfile();
    if (!storedProfile) return false;
    setProfile(storedProfile);
    setLogRocketUserIdentifier(storedProfile);

    try {
      const storedImageRaw = localStorage.getItem('profileStaffImage');
      if (storedImageRaw) {
        const storedImage: string = JSON.parse(storedImageRaw);
        setProfileImage(storedImage);
      }
    } catch (e) {
      console.error('Invalid stored staff profile image:', e);
    }

    setProfileStatus('COMPLETED');
    return true;
  }, []);

  const getProfile = useCallback(async () => {
    if (!userInfo || isAccessDenied) return;
    try {
      profileFetchInitiated.current = true;
      setProfileStatus('LOADING');
      const profile = await profileMutation.mutateAsync({
        username: userInfo.email as string,
      });
      setProfile(profile);
      setLogRocketUserIdentifier(profile);
      try {
        const profileImageRes = await profileImageMutation.mutateAsync({
          userId: profile.value[0].eXpMember.Uuid,
        });
        const { memberKeyNumeric, teamKeyNumeric } =
          extractMemberKeys(profileImageRes);

        if (memberKeyNumeric !== undefined || teamKeyNumeric !== undefined) {
          setProfile((prev) => {
            if (!prev?.value?.[0]) return prev;
            return {
              ...prev,
              value: [
                {
                  ...prev.value[0],
                  MemberKeyNumeric:
                    memberKeyNumeric ?? prev.value[0].MemberKeyNumeric,
                  TeamKeyNumeric:
                    teamKeyNumeric ?? prev.value[0].TeamKeyNumeric,
                },
              ],
            };
          });
        }

        const imagesRes = await imagesMutation.mutateAsync({
          url: profileImageRes.value[0].AgentPhotoUrl,
        });
        setProfileImage(imagesRes);
      } catch (piError) {
        console.error('Error fetching profile image:', piError);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setProfileStatus('COMPLETED');
    }
  }, [
    userInfo,
    profileMutation,
    profileImageMutation,
    imagesMutation,
    isAccessDenied,
  ]);

  useEffect(() => {
    if (profileStatus !== 'IDLE' || profileFetchInitiated.current) return;
    if (isStaffEnv) {
      profileFetchInitiated.current = true;
      const hydrated = hydrateFromLocalStorage();
      if (!hydrated) {
        if (userInfo?.email) {
          setProfile(initialValue(userInfo));
          setProfileStatus('COMPLETED');
        } else {
          setProfileStatus('LOADING');
        }
      }
      return;
    }
    if (authState?.idToken?.idToken) {
      getProfile();
    }
  }, [
    authState?.idToken?.idToken,
    profileStatus,
    getProfile,
    isStaffEnv,
    hydrateFromLocalStorage,
    userInfo,
  ]);

  useEffect(() => {
    if (!isStaffEnv) return;
    if (!profileFetchInitiated.current) return;
    const hasProfileEmail = Boolean(profile?.value?.[0]?.MemberEmail);
    if (!hasProfileEmail && userInfo?.email) {
      setProfile(initialValue(userInfo));
      if (profileStatus !== 'COMPLETED') {
        setProfileStatus('COMPLETED');
      }
    }
  }, [isStaffEnv, userInfo, profile, profileStatus]);

  // In staff env, always identify LogRocket with the admin's Okta identity,
  // regardless of which user is being impersonated.
  useEffect(() => {
    if (!isStaffEnv || !userInfo?.email || staffAdminIdentified.current) return;
    logRocketUtil.identifyStaffAdmin(userInfo);
    staffAdminIdentified.current = true;
  }, [isStaffEnv, userInfo]);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        profileStatus,
        profileImage,
        setProfile,
        setProfileImage,
      }}
      data-testid="profile-context-provider"
    >
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileProvider;
